import { __decorate } from "tslib";
import { Component, Prop, Vue } from 'vue-property-decorator';
import CharacterBuilder from './CharacterBuilder/CharacterBuilder.vue';
import CharacterSheet from './CharacterSheet/CharacterSheet.vue';
import Loading from '@/components/Loading.vue';
import { namespace } from 'vuex-class';
import { merge, get, set, omit } from 'lodash';
import BackButton from '@/components/BackButton.vue';
const characterModule = namespace('character');
const classesModule = namespace('classes');
const archetypesModule = namespace('archetypes');
const speciesModule = namespace('species');
const equipmentModule = namespace('equipment');
const powersModule = namespace('powers');
const featsModule = namespace('feats');
const featuresModule = namespace('features');
const backgroundsModule = namespace('backgrounds');
const characterAdvancementsModule = namespace('characterAdvancements');
const skillsModule = namespace('skills');
const conditionsModule = namespace('conditions');
const enhancedItemsModule = namespace('enhancedItems');
const authenticationModule = namespace('authentication');
let Characters = class Characters extends Vue {
    constructor() {
        super(...arguments);
        this.hasFetchedData = false;
        this.isEditing = true;
        this.isDirty = false;
        this.currentStep = 0;
    }
    created() {
        Promise.all([
            this.fetchClasses(),
            this.fetchArchetypes(),
            this.fetchEquipment(),
            this.fetchPowers(),
            this.fetchFeats(),
            this.fetchFeatures(),
            this.fetchBackgrounds(),
            this.fetchSpecies(),
            this.fetchCharacterAdvancements(),
            this.fetchSkills(),
            this.fetchConditions(),
            this.fetchEnhancedItems()
        ]).then(() => {
            if (!this.character) {
                window.alert('Character not found');
                this.$router.push('/tools/mycharacters');
            }
            this.hasFetchedData = true;
            this.isEditing = !this.characterValidation.isValid;
            this.isDirty = !this.isLoggedIn || (this.isNew === 'true' && this.isEditing);
            this.currentStep = this.getIsEmptyCharacter(this.character) ? 0 : 1;
        });
    }
    get character() {
        return this.getCharacterById(this.characterId);
    }
    get completeCharacter() {
        return this.character ? this.generateCompleteCharacter(this.character) : null;
    }
    get characterValidation() {
        return this.getCharacterValidation(this.character);
    }
    get title() {
        return ((this.character && this.character.name) || 'Characters') + Vue.prototype.$titleSuffix;
    }
    goToStep(step) {
        this.isEditing = true;
        this.currentStep = step;
        window.scrollTo(0, 0);
    }
    goToSheet() {
        this.isEditing = false;
        if (this.isLoggedIn)
            this.handleSaveCharacter();
        else if (this.character)
            this.saveCharacterLocally(this.character);
        window.scrollTo(0, 0);
    }
    saveCharacterIfDone(newCharacter) {
        if (this.isEditing || !this.isLoggedIn) {
            this.isDirty = true;
            this.saveCharacterLocally(newCharacter);
        }
        else {
            this.isDirty = false;
            this.saveCharacter(newCharacter);
        }
    }
    updateCharacter(newCharacter) {
        this.saveCharacterIfDone(merge({}, this.character, newCharacter));
    }
    replaceCharacterProperty({ path, property }) {
        let characterCopy = merge({}, this.character);
        set(characterCopy, path, property);
        this.saveCharacterIfDone(characterCopy);
    }
    replaceCharacterProperties(replacements) {
        let characterCopy = merge({}, this.character);
        replacements.forEach(({ path, property }) => set(characterCopy, path, property));
        this.saveCharacterIfDone(characterCopy);
    }
    deleteCharacterProperty({ path, index }) {
        const oldProperty = get(this.character, path);
        let property = omit(oldProperty, index);
        if (Array.isArray(oldProperty))
            property = Object.values(property);
        this.replaceCharacterProperty({ path, property });
    }
    handleSaveCharacter() {
        if (this.character) {
            this.isDirty = false;
            this.saveCharacter(this.character);
        }
    }
    handleDeleteCharacter() {
        if (this.character) {
            this.deleteCharacter(this.character).then(() => this.$router.push('/tools/mycharacters'));
        }
    }
};
__decorate([
    Prop(String)
], Characters.prototype, "characterId", void 0);
__decorate([
    Prop(String)
], Characters.prototype, "isNew", void 0);
__decorate([
    characterModule.State
], Characters.prototype, "characters", void 0);
__decorate([
    characterModule.Getter
], Characters.prototype, "generateCompleteCharacter", void 0);
__decorate([
    characterModule.Getter
], Characters.prototype, "getCharacterById", void 0);
__decorate([
    characterModule.Getter
], Characters.prototype, "getIsEmptyCharacter", void 0);
__decorate([
    characterModule.Getter
], Characters.prototype, "getCharacterValidation", void 0);
__decorate([
    characterModule.Action
], Characters.prototype, "saveCharacter", void 0);
__decorate([
    characterModule.Action
], Characters.prototype, "saveCharacterLocally", void 0);
__decorate([
    characterModule.Action
], Characters.prototype, "deleteCharacter", void 0);
__decorate([
    authenticationModule.Getter
], Characters.prototype, "isLoggedIn", void 0);
__decorate([
    classesModule.State
], Characters.prototype, "classes", void 0);
__decorate([
    classesModule.Action
], Characters.prototype, "fetchClasses", void 0);
__decorate([
    archetypesModule.State
], Characters.prototype, "archetypes", void 0);
__decorate([
    archetypesModule.Action
], Characters.prototype, "fetchArchetypes", void 0);
__decorate([
    speciesModule.State
], Characters.prototype, "species", void 0);
__decorate([
    speciesModule.Action
], Characters.prototype, "fetchSpecies", void 0);
__decorate([
    equipmentModule.State
], Characters.prototype, "equipment", void 0);
__decorate([
    equipmentModule.Action
], Characters.prototype, "fetchEquipment", void 0);
__decorate([
    powersModule.State
], Characters.prototype, "powers", void 0);
__decorate([
    powersModule.Action
], Characters.prototype, "fetchPowers", void 0);
__decorate([
    featsModule.State
], Characters.prototype, "feats", void 0);
__decorate([
    featsModule.Action
], Characters.prototype, "fetchFeats", void 0);
__decorate([
    featuresModule.State
], Characters.prototype, "features", void 0);
__decorate([
    featuresModule.Action
], Characters.prototype, "fetchFeatures", void 0);
__decorate([
    backgroundsModule.State
], Characters.prototype, "backgrounds", void 0);
__decorate([
    backgroundsModule.Action
], Characters.prototype, "fetchBackgrounds", void 0);
__decorate([
    characterAdvancementsModule.Action
], Characters.prototype, "fetchCharacterAdvancements", void 0);
__decorate([
    skillsModule.Action
], Characters.prototype, "fetchSkills", void 0);
__decorate([
    conditionsModule.Action
], Characters.prototype, "fetchConditions", void 0);
__decorate([
    enhancedItemsModule.Action
], Characters.prototype, "fetchEnhancedItems", void 0);
Characters = __decorate([
    Component({
        components: {
            CharacterBuilder,
            CharacterSheet,
            BackButton,
            Loading
        }
    })
], Characters);
export default Characters;
